import type { ConstructionPhaseStatus } from '../types';

export type ConstructionPhaseData = {
  category: string;
  phases: {
    name: string;
    duration: string;
    possibleStatuses: ConstructionPhaseStatus[];
  }[];
};

/**
 * Default construction phase template used when seeding a new build project.
 *
 * Lives here rather than in `pages/ProjectDetails.tsx` because `projectsStore`
 * needs it, `projectsStore` is pulled in eagerly by `AppInitializer`, and
 * importing it from that page dragged the entire 120 KB staff screen — plus
 * leaflet, ~440 KB — into the entry chunk that every portal customer downloads
 * before first paint.
 */
export const constructionPhases: ConstructionPhaseData[] = [
  {
    category: "Pre-Construction",
    phases: [
      { name: "Site Selection and Survey", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Finalizing Floor Plan and Design", duration: "2-4 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Contract Agreement", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Site Preparation",
    phases: [
      { name: "Clearing the Site", duration: "1-2 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Marking Foundation Layout", duration: "1-2 days", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Foundation Work",
    phases: [
      { name: "Excavation for Foundation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Earth Filling and Compaction", duration: "2-4 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "PCC (Plain Cement Concrete) Layer", duration: "2-3 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Foundation Reinforcement", duration: "3-5 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Footing Casting", duration: "3-4 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "DPC (Damp Proof Course)", duration: "1-2 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Plinth Beam Construction", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Superstructure Construction",
    phases: [
      { name: "Superstructure Brickwork", duration: "3-5 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Column Casting", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Beam Casting", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Slab Shuttering and Reinforcement", duration: "2-3 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Slab Casting", duration: "3-4 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Curing of Concrete", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Staircase & Roof Work",
    phases: [
      { name: "Staircase Formwork", duration: "3-5 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Staircase Casting", duration: "2-3 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "First Floor Brickwork", duration: "3-4 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Roof Beam Construction", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Roof Slab Casting", duration: "3-4 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Roof Parapet Wall Construction", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Utility Installations (Electrical & Plumbing)",
    phases: [
      { name: "Electrical Conduits Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Plumbing and Drainage Installation", duration: "2-3 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Waterproofing & Plastering",
    phases: [
      { name: "Waterproofing of Slabs and Walls", duration: "3-5 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Plastering (External and Internal)", duration: "2-3 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Finishing & Interior Work",
    phases: [
      { name: "Window and Door Frames Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Lintel Casting", duration: "3-5 days", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Flooring Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Ceiling Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Painting (Primer and Finish Coat)", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Door and Window Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Final Utilities & Fixtures",
    phases: [
      { name: "Electrical Wiring and Switch Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Plumbing Fixtures Installation", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Sanitary and Ventilation Systems", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  },
  {
    category: "Final Touches & Handover",
    phases: [
      { name: "Final Touches and Finishing Works", duration: "1-2 weeks", possibleStatuses: ["not_started", "in_progress", "completed"] },
      { name: "Final Inspection and Handover", duration: "1 week", possibleStatuses: ["not_started", "in_progress", "completed"] }
    ]
  }
];
